/**
 * WaveSelector - Composant réutilisable pour sélection de forme d'onde
 * Affiche des boutons avec icônes Unicode et label
 */

// Mapping des formes d'onde vers des symboles Unicode
const WAVE_ICONS = {
    sawtooth: '⩘',    // Sawtooth approximation
    square: '⊓',       // Square wave
    triangle: '△',     // Triangle
    sine: '∿',         // Sine wave
    pulse: '⊓',        // Pulse (alias for square)
    noise: '⁂'         // Noise
};

export class WaveSelector {
    /**
     * @param {HTMLElement} container - Element parent
     * @param {Object} options
     * @param {Array} options.waveforms - [{value: 'sawtooth', label: 'SAW'}, ...]
     * @param {string} options.value - Valeur initiale
     * @param {string} options.label - Label affiché sous les boutons
     * @param {string} options.idPrefix - Préfixe pour les IDs (optionnel)
     * @param {boolean} options.showIcons - Afficher les icônes Unicode (default: true)
     */
    constructor(container, options) {
        this.container = container;
        this.waveforms = options.waveforms || [
            { value: 'sawtooth', label: 'SAW' },
            { value: 'square', label: 'SQR' },
            { value: 'triangle', label: 'TRI' },
            { value: 'sine', label: 'SIN' }
        ];
        this.value = options.value || this.waveforms[0].value;
        this.label = options.label || 'Waveform';
        this.idPrefix = options.idPrefix || `wave_${Date.now()}`;
        this.showIcons = options.showIcons !== false;
        this.callbacks = [];

        this.element = null;
        this.buttons = [];

        this._render();
    }

    _render() {
        // Container groupe
        this.element = document.createElement('div');
        this.element.className = 'knob-group wave-group';

        // Selector avec boutons
        const selector = document.createElement('div');
        selector.className = 'wave-selector';
        selector.id = `${this.idPrefix}_selector`;

        // Créer un bouton par waveform
        this.waveforms.forEach(wf => {
            const btn = document.createElement('button');
            btn.className = `wave-btn ${this.value === wf.value ? 'active' : ''}`;
            btn.dataset.wave = wf.value;
            btn.title = wf.label;

            // Contenu du bouton: icône + label ou juste label
            if (this.showIcons && WAVE_ICONS[wf.value]) {
                btn.innerHTML = `<span class="wave-icon">${WAVE_ICONS[wf.value]}</span><span class="wave-label">${wf.label}</span>`;
            } else {
                btn.textContent = wf.label;
            }

            btn.addEventListener('click', () => this._selectWave(wf.value));
            selector.appendChild(btn);
            this.buttons.push(btn);
        });

        this.element.appendChild(selector);

        // Label en dessous
        const labelEl = document.createElement('span');
        labelEl.className = 'knob-label';
        labelEl.textContent = this.label;
        this.element.appendChild(labelEl);

        this.container.appendChild(this.element);
    }

    _selectWave(value) {
        if (value === this.value) return;

        this.value = value;

        // Mettre à jour l'état visuel
        this.buttons.forEach(btn => {
            btn.classList.toggle('active', btn.dataset.wave === value);
        });

        // Notifier les callbacks
        this.callbacks.forEach(cb => cb(value));
    }

    // API publique compatible avec les autres composants

    getValue() {
        return this.value;
    }

    setValue(value) {
        if (this.waveforms.some(wf => wf.value === value)) {
            this._selectWave(value);
        }
        return this;
    }

    onChange(callback) {
        this.callbacks.push(callback);
        return this;
    }

    destroy() {
        if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }
        this.buttons = [];
        this.callbacks = [];
    }
}

// Factory function pour usage simplifié
export function createWaveSelector(container, options) {
    return new WaveSelector(container, options);
}
